// Shared rendering helpers for the /chat page and the bubble drawer.
// DOM shapes match the React redesign at
//   ~/Developer/work/autoboost/redesign/chat-design/chat-conversation.jsx
// — port faithfully, no improvisation. Two surfaces (page + bubble)
// consume the SAME builders so a typography tweak here updates both.
//
// Public exports:
//   buildMessageRow(msg)   → DocumentFragment for one ChatMessage
//   buildToolLedger(tools) → DocumentFragment for a contiguous tool-call group
//   buildThinkingRow(label) → "the assistant is thinking…" indicator
//   buildDaySeparator(label) → "Today — 2:14 PM" horizontal rule
//   buildRound({user, tools, assistant}) → wraps a full turn
//   renderMarkdown(content) → styled HTML element
//   labelFor(slug)         → friendly label for a tool slug
//   csrfToken()            → meta-csrf helper for fetch headers
//   TOOL_LABELS            → slug → label map (for admin tweaks)
//
// metadata.shape primitives (HeroNumber / MiniTable / CompareStrip /
// StatusHeadline / ConfirmRow) render ABOVE the markdown body inside
// the assistant turn. If metadata.shape is absent, only markdown
// renders — backend tunes the agent to emit shapes in v2.

import { marked } from "marked"
import {
  ICON_SPARK12, ICON_CHECK, ICON_ALERT, ICON_WARN, ICON_TREND,
  ICON_SPINNER, ICON_COPY,
} from "./chat_icons"

marked.setOptions({ breaks: true, gfm: true })

// ----------------------------------------------------------------------
// Tool-slug → friendly label. Mirrors TOOL_LABELS in chat-conversation.jsx.
// Unknown slugs fall back to a humanized split of the camelCase name.
// ----------------------------------------------------------------------
export const TOOL_LABELS = {
  GetUserProfileTool: "Checked your account",
  ListCampaignsTool: "Pulled your campaigns",
  GetCampaignInsightsTool: "Loaded campaign performance",
  ListAdsTool: "Listed your ads",
  ListPostsTool: "Pulled recent posts",
  GetAdAccountTool: "Loaded ad account",
  ListAutoboostsTool: "Checked your autoboosts",
  GetAutoboostTriggerHistoryTool: "Reviewed trigger history",
  DiagnoseSyncTool: "Ran sync diagnostics",
}

export function labelFor(slug) {
  if (!slug) return "tool"
  if (TOOL_LABELS[slug]) return TOOL_LABELS[slug]
  return String(slug).replace(/Tool$/, "").replace(/([A-Z])/g, " $1").trim()
}

export function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content || ""
}

function escapeHtml(text) {
  const d = document.createElement("div")
  d.textContent = text == null ? "" : String(text)
  return d.innerHTML
}

// ----------------------------------------------------------------------
// Markdown rendering — Tailwind-compatible class injection.
// All output classes live under `.chat-md` in application.css so a
// global typography tweak lands in one place.
// ----------------------------------------------------------------------
export function renderMarkdown(content) {
  const html = marked.parse(content || "")
  const container = document.createElement("div")
  container.className = "chat-md"
  container.innerHTML = html
  container.querySelectorAll("a").forEach((a) => {
    a.target = "_blank"
    a.rel = "noopener noreferrer"
  })
  return container
}

// ----------------------------------------------------------------------
// Inline data primitives — gated by msg.metadata.shape.
// One primitive per turn matches the design's MidConversation example.
// ----------------------------------------------------------------------
function renderHeroNumber(shape) {
  const el = document.createElement("div")
  el.className = "chat-hero-number"
  el.innerHTML =
    `<div class="chat-hero-number-value">${escapeHtml(shape.value)}</div>` +
    `<div class="chat-hero-number-label">${escapeHtml(shape.label || "")}</div>`
  if (shape.delta) {
    const dir = shape.delta_dir === "down" ? "down" : "up"
    el.insertAdjacentHTML(
      "beforeend",
      `<span class="chat-hero-number-delta ${dir}">${dir === "down" ? "↓" : "↑"} ${escapeHtml(shape.delta)}</span>`,
    )
  }
  return el
}

function renderCompareStrip(shape) {
  const el = document.createElement("div")
  el.className = "chat-compare-strip"
  const dir = shape.dir === "down" ? "down" : "up"
  el.innerHTML =
    `<div class="col then"><div class="lbl">${escapeHtml(shape.then_label)}</div><div class="val">${escapeHtml(shape.then_value)}</div></div>` +
    `<div class="arrow ${dir}">${ICON_TREND}<span>${escapeHtml(shape.delta || "")}</span></div>` +
    `<div class="col now"><div class="lbl">${escapeHtml(shape.now_label)}</div><div class="val">${escapeHtml(shape.now_value)}</div></div>`
  return el
}

function renderStatusHeadline(shape) {
  const el = document.createElement("div")
  const tone = ["ok", "warn", "bad"].includes(shape.tone) ? shape.tone : "warn"
  el.className = `chat-status-headline ${tone}`
  const iconSvg = tone === "ok" ? ICON_CHECK : tone === "bad" ? ICON_ALERT : ICON_WARN
  el.innerHTML = `${iconSvg}<span>${escapeHtml(shape.text || "")}</span>`
  return el
}

function renderMiniTable(shape) {
  const headers = Array.isArray(shape.headers) ? shape.headers : []
  const rows = Array.isArray(shape.rows) ? shape.rows : []
  const theadCells = headers.map((h) => {
    const label = typeof h === "string" ? h : (h.label || "")
    const align = typeof h === "object" && h.align === "right" ? ' style="text-align:right"' : ""
    return `<th${align}>${escapeHtml(label)}</th>`
  }).join("")
  const tbodyRows = rows.map((row) =>
    `<tr>${row.map((cell, j) => {
      const h = headers[j] || {}
      const isRight = typeof h === "object" && h.align === "right"
      if (cell && typeof cell === "object") {
        if (cell.type === "bar") {
          const px = Math.max(8, (Number(cell.percent) || 0) * 0.6)
          return `<td class="num"><span style="display:inline-flex;align-items:center;gap:8px"><span>${escapeHtml(cell.value)}</span><span class="bar" style="width:${px}px"></span></span></td>`
        }
        if (cell.type === "pill") {
          return `<td><span class="ad-pill">${escapeHtml(cell.value)}</span></td>`
        }
      }
      return `<td class="${isRight ? "num" : ""}">${escapeHtml(cell)}</td>`
    }).join("")}</tr>`,
  ).join("")
  const el = document.createElement("div")
  el.className = "chat-mini-table"
  el.innerHTML = `<table><thead><tr>${theadCells}</tr></thead><tbody>${tbodyRows}</tbody></table>`
  return el
}

function renderConfirmRow(shape) {
  const el = document.createElement("div")
  el.className = "chat-confirm-row"
  el.innerHTML =
    `<div class="q">${escapeHtml(shape.question || "")}</div>` +
    `<button type="button" class="btn-skip">${escapeHtml(shape.skip_label || "Skip")}</button>` +
    `<button type="button" class="btn-confirm">${escapeHtml(shape.confirm_label || "Confirm")}</button>`
  return el
}

function renderShape(shape) {
  if (!shape || typeof shape !== "object") return null
  switch (shape.kind) {
    case "hero_number":     return renderHeroNumber(shape)
    case "compare_strip":   return renderCompareStrip(shape)
    case "status_headline": return renderStatusHeadline(shape)
    case "mini_table":      return renderMiniTable(shape)
    case "confirm_row":     return renderConfirmRow(shape)
    default: return null
  }
}

// ----------------------------------------------------------------------
// Tool ledger — contiguous tool chips ABOVE the assistant turn they
// belong to. Accepts an array of either persisted "tool" ChatMessage
// rows OR runtime tool objects shaped as { slug, state, duration }.
// ----------------------------------------------------------------------
export function buildToolLedger(tools) {
  if (!tools || !tools.length) return null
  const el = document.createElement("div")
  el.className = "chat-tool-ledger"
  el.setAttribute("aria-label", "Tools the assistant used")
  tools.forEach((t) => {
    const slug = t.tool_name || t.slug
    const state = t.state || (t.metadata && t.metadata.error ? "error" : "done")
    const duration = t.duration || (t.metadata && t.metadata.duration) || null
    const chip = document.createElement("span")
    chip.className = `chat-tool-chip ${state}`
    chip.title = slug + (t.tool_input ? " · " + JSON.stringify(t.tool_input) : "")
    const iconSvg = state === "running" ? ICON_SPINNER : state === "error" ? ICON_ALERT : ICON_CHECK
    chip.innerHTML =
      `<span class="chat-tool-chip-icon">${iconSvg}</span>` +
      `<span>${escapeHtml(labelFor(slug))}</span>` +
      (duration ? `<span class="chat-tool-chip-duration">${escapeHtml(duration)}</span>` : "")
    el.appendChild(chip)
  })
  return el
}

// ----------------------------------------------------------------------
// User turn — right-aligned navy bubble.
// ----------------------------------------------------------------------
function buildUserTurn(msg) {
  const row = document.createElement("div")
  row.className = "chat-user-turn"
  const bubble = document.createElement("div")
  bubble.className = "chat-user-bubble"
  bubble.textContent = msg.content || ""
  row.appendChild(bubble)
  return row
}

// ----------------------------------------------------------------------
// Assistant turn — memo style with avatar, body (shape + markdown),
// footer (Copy only — Regenerate + Thumbs hidden per CLAUDE.md ruling).
// ----------------------------------------------------------------------
function buildAssistantTurn(msg) {
  const row = document.createElement("div")
  row.className = "chat-assistant-turn"

  const avatar = document.createElement("div")
  avatar.className = "chat-assistant-avatar"
  avatar.innerHTML = ICON_SPARK12
  row.appendChild(avatar)

  const body = document.createElement("div")
  body.className = "chat-assistant-body"

  // metadata.shape goes ABOVE the markdown body, per the React design's
  // MidConversation example. Absent shape → markdown-only render.
  const shapeNode = renderShape(msg.metadata && msg.metadata.shape)
  if (shapeNode) body.appendChild(shapeNode)

  const md = renderMarkdown(msg.content || "")
  body.appendChild(md)

  // Footer — Copy only. Fades in on .chat-assistant-turn:hover.
  const footer = document.createElement("div")
  footer.className = "chat-turn-footer"
  const copyBtn = document.createElement("button")
  copyBtn.type = "button"
  copyBtn.title = "Copy reply"
  copyBtn.innerHTML = `${ICON_COPY}<span>Copy</span>`
  copyBtn.addEventListener("click", () => {
    if (!navigator.clipboard) return
    navigator.clipboard.writeText(msg.content || "").then(() => {
      const span = copyBtn.querySelector("span")
      const prev = span.textContent
      span.textContent = "Copied"
      setTimeout(() => { span.textContent = prev }, 1500)
    }).catch((e) => console.error("[chat-render] copy failed:", e))
  })
  footer.appendChild(copyBtn)

  if (msg.model || msg.input_tokens || msg.output_tokens) {
    const meta = document.createElement("span")
    meta.className = "meta"
    const tokens = (Number(msg.input_tokens) || 0) + (Number(msg.output_tokens) || 0)
    meta.textContent = `${msg.model || ""}${tokens ? " · " + tokens + " tokens" : ""}`.trim()
    footer.appendChild(meta)
  }
  body.appendChild(footer)

  row.appendChild(body)
  return row
}

// ----------------------------------------------------------------------
// System / error row — red banner.
// ----------------------------------------------------------------------
function buildSystemRow(msg) {
  const row = document.createElement("div")
  row.className = "chat-system-row"
  row.innerHTML = `${ICON_ALERT}<span>${escapeHtml(msg.content || "Something went wrong.")}</span>`
  return row
}

// ----------------------------------------------------------------------
// Public single-message builder. Tool rows return null — callers
// should collect them and use buildToolLedger above the parent
// assistant turn.
// ----------------------------------------------------------------------
export function buildMessageRow(msg) {
  if (!msg) return null
  switch (msg.role) {
    case "user":      return buildUserTurn(msg)
    case "assistant": return buildAssistantTurn(msg)
    case "system":    return buildSystemRow(msg)
    case "tool":      return null
    default: return null
  }
}

// ----------------------------------------------------------------------
// Group helpers — wrap a contiguous user → tool* → assistant trio in
// a `.chat-round` block.
// ----------------------------------------------------------------------
export function buildRound({ user, tools, assistant }) {
  const round = document.createElement("div")
  round.className = "chat-round"
  if (user) {
    const u = buildMessageRow(user)
    if (u) round.appendChild(u)
  }
  if (tools && tools.length) {
    const t = buildToolLedger(tools)
    if (t) round.appendChild(t)
  }
  if (assistant) {
    const a = buildMessageRow(assistant)
    if (a) round.appendChild(a)
  }
  return round
}

// ----------------------------------------------------------------------
// Thinking indicator — pulsing avatar + label + bouncing dots.
// ----------------------------------------------------------------------
export function buildThinkingRow(label = "Thinking…") {
  const row = document.createElement("div")
  row.className = "chat-thinking"
  const avatar = document.createElement("div")
  avatar.className = "chat-assistant-avatar"
  avatar.innerHTML = ICON_SPARK12
  row.appendChild(avatar)
  const lbl = document.createElement("div")
  lbl.className = "chat-thinking-label"
  lbl.innerHTML = `<span>${escapeHtml(label)}</span><span class="chat-thinking-dots"><span></span><span></span><span></span></span>`
  row.appendChild(lbl)
  return row
}

// ----------------------------------------------------------------------
// Day separator — "Today — 2:14 PM" between rounds.
// ----------------------------------------------------------------------
export function buildDaySeparator(label) {
  const el = document.createElement("div")
  el.className = "chat-day-sep"
  el.textContent = label || ""
  return el
}
